import { fromMultipleQueryObjects } from 'app/support/searchParameterMapping';
import {
  availableSearchParametersForSearch,
  enforceAvailabilityForParameters,
} from 'app/support/searchParameterAvailability';
import availableQueryCorrections from 'app/support/availableQueryCorrections';
import setDefaults from 'app/support/searchParameterDefaults';
import metadataMapper from './metadataMapper';

const prepareSearchParameters = (paramObjects, searchProfile) => {
  const searchParameters = fromMultipleQueryObjects(paramObjects);
  const availableSearchParameters = availableSearchParametersForSearch(
    searchParameters,
    searchProfile
  );
  const searchParametersWithDefaults = setDefaults(
    searchParameters,
    availableSearchParameters
  );
  const enforcedSearchParameters = enforceAvailabilityForParameters(
    availableSearchParameters,
    searchParametersWithDefaults
  );

  return {
    availableSearchParameters,
    searchParameters: enforcedSearchParameters,
  };
};

// This function is used to handle the combination family=editorial and assettype=image,film
// as this is not a supported search scenario
const handleQueryCorrections = (queryCorrections, queryParams) => {
  if (!queryCorrections?.family || queryParams.assettype !== 'image,film') {
    return queryCorrections;
  }

  return {
    ...queryCorrections,
    assettype: 'image',
  };
};

export default (results) => {
  const updatedQueryCorrections = handleQueryCorrections(
    results.gallery.queryCorrections,
    results.query.params
  );

  const processedQueryCorrections = availableQueryCorrections(
    results.query,
    updatedQueryCorrections
  );

  const paramsFromMetadata = metadataMapper(
    results.query.metadata,
    results.query.params
  );

  // the order of the paramSources is important as the parameter values from preceding sources always take precedence
  const paramSources = [
    updatedQueryCorrections,
    paramsFromMetadata,
    results.query.routeParams,
    results.query.params,
  ];

  const searchParameters = prepareSearchParameters(
    paramSources,
    results.searchProfile
  );

  return {
    hasCustomMediaAgreements: results.hasCustomMediaAgreements,
    assets: results.gallery.assets,
    availableSearchParameters: searchParameters.availableSearchParameters,
    breadcrumbs: results.breadcrumbs,
    clarifications: results.gallery.clarifications,
    completedSearchParameters: searchParameters.searchParameters,
    gsrpIndexableContentTypeLinks: results.gsrpIndexableContentTypeLinks,
    continent: results.continent,
    countryLanguagePrefix: results.countryLanguagePrefix,
    counts: results.gallery.counts,
    dropboxApp: results.dropboxApp,
    events: results.gallery.events,
    hideRelatedTermsInGallery: results.hideRelatedTermsInGallery,
    isSeoOptimizedPath: results.isSeoOptimizedPath,
    lastPage: results.gallery.lastPage,
    name: results.gallery.name,
    pageMetaData: results.pageMetaData,
    parentEvent: results.gallery.parentEvent,
    phraseProcessing: results.gallery.phraseProcessing,
    preferSearch2Paths: results.preferSearch2Paths,
    queriedKeywordsByClause: results.gallery.queriedKeywordsByClause,
    queryCorrections: processedQueryCorrections,
    relatedTerms: results.relatedTerms,
    relatedTermsViewModel: results.relatedTermsViewModel,
    renderGatewayView: results.renderGatewayView,
    searchParameters: searchParameters.searchParameters,
    searchProfile: results.searchProfile,
    searchSafeImageSource: results.searchSafeImageSource,
    seoCustomContent: results.seoCustomContent,
    seoHeadlineCopySegments: results.seoHeadlineCopySegments,
    showGatewayAssetDescription: results.showGatewayAssetDescription,
    showNLSExample: results.showNLSExample,
    similarAsset: results.gallery.similarAsset,
    title: results.title,
    totalResults: results.gallery.totalNumberOfResults,
    undoLangCorrectionData: results.undoLangCorrectionData,
  };
};
